import PageHero from "@/components/PageHero";
import { COMPANY_VALUES } from "@/data/site";
import CTASection from "@/sections/CTASection";
import { motion } from "framer-motion";
import { Target, Eye, Heart } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=85&w=2400";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About NEPHRC"
        title={<>Multidisciplinary consultancy.<br /><span className="italic font-light text-gold">Singular accountability.</span></>}
        subtitle="NEPHRC — North East Projects, Hospitality & Retails Consultancy — partners with governments, hospitality groups and retail operators across India to engineer outcomes that endure."
        image={ABOUT_IMG}
        breadcrumbs={[{ label: "About" }]}
      />

      <section className="bg-white py-24 sm:py-32">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="overline text-emerald-brand">Our story</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-4 leading-[1.05]">
              Engineering excellence, hospitality intelligence, retail intuition.
            </h2>
            <div className="mt-8 space-y-6 text-base sm:text-lg text-slate-brand leading-relaxed">
              <p>
                NEPHRC was founded with a simple conviction — that India&apos;s next wave of infrastructure, hospitality and retail deserves consultancy partners who combine global engineering discipline with deep local understanding.
              </p>
              <p>
                Over two decades, our practice has grown from a regional engineering office in the North East to a multi-city consultancy with a portfolio that spans mobility corridors, smart cities, luxury resorts, retail destinations and cultural landmarks.
              </p>
              <p>
                We remain founder-led, multidisciplinary by design, and intensely accountable to outcomes. Every commission — small or large — carries the same engineering rigour, transparent communication and client-first instinct.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <img src={ABOUT_IMG} alt="NEPHRC engineering team" className="w-full aspect-[4/5] object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-mist py-24 sm:py-32">
        <div className="container-x">
          <div className="grid lg:grid-cols-3 gap-6">
            <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5}} className="bg-white p-10 border-t-2 border-emerald-brand">
              <Eye className="text-emerald-brand" size={28} />
              <div className="overline text-emerald-brand mt-6">Vision</div>
              <p className="mt-4 font-display text-xl text-navy leading-snug">
                To be India&apos;s most trusted multidisciplinary consultancy — shaping infrastructure, hospitality and retail that strengthens communities for generations.
              </p>
            </motion.div>
            <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5, delay:0.1}} className="bg-white p-10 border-t-2 border-gold">
              <Target className="text-gold" size={28} />
              <div className="overline text-gold mt-6">Mission</div>
              <p className="mt-4 font-display text-xl text-navy leading-snug">
                Deliver consultancy excellence with transparent processes, sustainable practices and an unwavering client-first orientation.
              </p>
            </motion.div>
            <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5, delay:0.2}} className="bg-navy text-white p-10 border-t-2 border-gold">
              <Heart className="text-gold" size={28} />
              <div className="overline text-gold mt-6">Promise</div>
              <p className="mt-4 font-display text-xl leading-snug">
                One accountable partner from feasibility to commissioning — never a hand-off, never a surprise.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-32">
        <div className="container-x">
          <div className="max-w-2xl">
            <div className="overline text-emerald-brand">Core values</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl text-navy mt-4 leading-[1.05]">The principles we lead with.</h2>
          </div>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
            {COMPANY_VALUES.map((v, i) => (
              <motion.div key={v.title} initial={{opacity:0,y:14}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4, delay:(i%3)*0.05}} className="bg-white p-8">
                <div className="font-mono-alt text-xs text-emerald-brand">{String(i+1).padStart(2,"0")}.</div>
                <h3 className="font-display font-bold text-xl text-navy mt-4">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-brand leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
